import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import {
  CURRENT_SOCKET_ID_VERSION,
  SOCKET_ID_VERSION_CONFIG_KEY,
  socketIdVersion,
} from "../../utils/dynamicSockets/dynamicSocketId";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { eligibleEntityIdsSchema } from "./eligible-entity-ids";
import { customerEvidenceMatchSchema } from "../accounts-receivable/customers/match-customer-with-evidence.step";

/**
 * Output keys reserved by the step itself; user-defined customer routes
 * cannot collide with these. Includes both the dynamic socket ids
 * (`top_pick`, `others`) and the flat eval/context fields the handler
 * always emits at the top level (`matched_customer_id`, `matching_reasoning`,
 * etc.) so that a route id can never accidentally clobber an eval field.
 */
export const IDENTIFY_CUSTOMER_RESERVED_SOCKET_IDS = [
  "top_pick",
  "others",
  "matched_customer_id",
  "matched_customer_name",
  "confidence_band",
  "match_score",
  "matching_reasoning",
  "closest_candidate_customer_id",
  "closest_candidate_customer_name",
] as const;

const customerRouteSchema = z.object({
  id: z
    .string()
    .regex(
      /^[a-z][a-z0-9_]*$/,
      "Route id must be lowercase alphanumeric with underscores (valid socket id)",
    )
    // The reserved-id check is NOT here: whether a reserved id is usable
    // depends on the step's socket-id version, which a field-level refinement
    // cannot see. It lives in the configSchema superRefine below.
    .describe(
      "Output socket id for this customer route. Lowercase snake_case.",
    ),
  customerId: z
    .uuid()
    .describe("Customer to route to this socket when matched."),
  label: z
    .string()
    .optional()
    .describe("Optional display label for the socket."),
});

/**
 * Identify Customer Step
 *
 * Wraps the AI-powered customer matching pipeline (OpenAI identifier
 * extraction + Pinecone embeddings + LLM disambiguation) and exposes the
 * result through dynamic per-customer output sockets so workflow authors
 * can branch directly on which customer was matched.
 *
 * Inputs are flexible — any combination of:
 *   - inboundEmail entity ref (loads attachments and builds From/To/Subject/Body
 *     context automatically)
 *   - documents array of CORE_DOCUMENT refs (signed URLs are fetched)
 *   - preExtractedIdentifiers (skips the extraction step when an upstream
 *     node already pulled possibleNames / emailDomains out)
 *   - additionalContext free-text (concatenated to whatever else is built)
 *
 * Outputs are generated dynamically from `config.customerRoutes` (one
 * socket per route) plus a fixed `top_pick` socket and an optional
 * `others` catch-all. See `generateIdentifyCustomerOutputSockets` in
 * `backend/src/workflows/sockets/dynamicOutputSockets.ts`.
 */
export const identifyCustomerStep = defineStepConfig({
  type: "identify_customer",
  name: "Identify Customer",
  description:
    "Identify the customer behind an email or document using AI extraction + embeddings, then route by matched customer",
  category: "AI & Agents",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL)
      .optional()
      .describe(
        "Inbound email to use for matching. Attachments and From/To/Subject/Body context are pulled in automatically.",
      ),
    documents: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .optional()
      .describe(
        "Documents to use for identifier extraction. Signed URLs are fetched on the backend.",
      ),
    preExtractedIdentifiers: z
      .object({
        possibleNames: z.array(z.string()).optional(),
        emailDomains: z.array(z.string()).optional(),
        emailAddresses: z.array(z.string()).optional(),
      })
      .optional()
      .describe(
        "Pre-extracted customer identifiers from an upstream step. Skips the AI extraction phase when provided.",
      ),
    additionalContext: z
      .string()
      .optional()
      .describe(
        "Free-text context appended to whatever inboundEmail/documents context the handler builds.",
      ),
    invoiceNumbers: z
      .array(z.string())
      .optional()
      .describe(
        "Invoice numbers the document cites, from an upstream step (a pre-normalized remittance has no extraction to read them from). Remittance policy only: merged with the document extraction's invoice numbers, and an invoice owned by exactly one customer corroborates that customer.",
      ),
    extractionResultId: z
      .uuid()
      .optional()
      .describe(
        "The extraction this run produced for the document. Read only when documentEvidence is this_run_extraction; absent there means the run has no extraction (a pre-normalized remittance).",
      ),
    knownCustomerMatch: customerEvidenceMatchSchema
      .nullable()
      .optional()
      .describe(
        "Remittance policy only: Match Customer With Evidence's match. A customer the source knows decides the customer unless the step's allowlist excludes it or the cited invoices all belong to other customers; sender memory is re-read at decision time.",
      ),
  }),

  // Output sockets are generated dynamically at template instantiation from
  // config.customerRoutes (one entity-ref socket per route) plus a fixed
  // `top_pick` json socket and an optional `others` socket. Uses z.record()
  // so dynamic keys survive Zod validation — same trick split_document_v2
  // and switch use.
  outputSchema: z.record(z.string(), z.any()),

  configSchema: z
    .object({
      matchingPolicy: z.enum(["standard", "remittance"]).default("standard"),
      documentEvidence: z
        .enum(["latest_extraction", "this_run_extraction"])
        .default("latest_extraction")
        .describe(
          "Remittance policy only: which extraction of the document supplies invoice numbers and payer evidence. latest_extraction is the document's most recent completed one; this_run_extraction is only the one wired on extractionResultId, so a run that extracted nothing never reads an older reading.",
        ),
      // Matching thresholds
      minNameScore: z
        .number()
        .min(0)
        .max(1)
        .default(0.55)
        .describe(
          "Minimum Pinecone score (after alias boost) for a candidate produced by a name search.",
        ),
      minEmailDomainScore: z
        .number()
        .min(0)
        .max(1)
        .default(0.45)
        .describe(
          "Minimum Pinecone score (after domain boost) for a candidate produced by an email-domain search.",
        ),
      minPickConfidenceScore: z
        .number()
        .min(0)
        .max(1)
        .default(0.6)
        .describe(
          "Minimum final match score required to surface a top pick. Below this the top_pick and route sockets use the not-taken sentinel so unmatched branches skip.",
        ),
      maxCandidates: z
        .number()
        .int()
        .min(1)
        .max(35)
        .default(20)
        .describe(
          "Maximum number of merged Pinecone + Postgres candidates passed to the LLM disambiguator. Postgres trigram can return many weak neighbors; a higher cap avoids dropping the right customer just outside the top N.",
        ),

      // Behavior toggles
      useLLMDisambiguation: z
        .boolean()
        .default(true)
        .describe(
          "When true (default), run LLM disambiguation when Pinecone+Postgres produce two or more merged candidates. Author instructions still force an LLM pass when there is only one candidate (for exclusion-style rules). When false, skip LLM unless additional instructions are set.",
        ),
      useHistoricalCorrections: z
        .boolean()
        .default(true)
        .describe(
          "When true, use org history of users changing the suggested customer on orders to optionally override the match.",
        ),
      additionalInstructions: z
        .string()
        .optional()
        .describe(
          "Extra instructions appended to the AI extraction and disambiguation prompts. When non-empty, an LLM disambiguation pass always runs first.",
        ),
      eligibleCustomerIds: eligibleEntityIdsSchema.describe(
        "When non-empty, matching only considers these customers. Empty means the full organization catalog. Use this to roll the workflow out to a subset of customers first.",
      ),

      // Routing
      customerRoutes: z
        .array(customerRouteSchema)
        .default([])
        .superRefine((routes, ctx) => {
          const seen = new Set<string>();
          routes.forEach((route, index) => {
            if (seen.has(route.id)) {
              ctx.addIssue({
                code: "custom",
                message: `Duplicate customerRoutes[].id: ${route.id}`,
                path: [index, "id"],
              });
              return;
            }
            seen.add(route.id);
          });
        })
        .describe(
          "Per-customer output sockets. Each route fires when the matched customer's id equals route.customerId.",
        ),
      enableOthersRoute: z
        .boolean()
        .default(false)
        .describe(
          "When true, an `others` socket is emitted for matches that don't match any configured route.",
        ),
      othersEmitNullWhenNoMatch: z
        .boolean()
        .default(false)
        .describe(
          "When enableOthersRoute is true: if true and identification produces no surfaced customer, `others` emits null so downstream steps on that wire still run. If false (default), `others` uses SOCKET_NOT_TAKEN when nothing matched (branch skips). Does not change `others` when a named route wins or when the catch-all customer ref is emitted.",
        ),
      // Declared so z.object() does not strip the stamp on parse. `executeStep`
      // parses the persisted config through this schema before handing it to the
      // handler, and the handler derives its payload keys from the version: drop
      // the stamp and it writes bare ids for sockets the canvas marked
      // `::dynamic`, so every per-route output reads undefined.
      [SOCKET_ID_VERSION_CONFIG_KEY]: z
        .number()
        .int()
        .optional()
        .describe(
          "Socket-id scheme this step was created under. Absent means the legacy scheme, where a route's name IS its output socket id.",
        ),
    })
    .superRefine((config, ctx) => {
      // On version 1 a route id IS its output socket id, so a route named after
      // one of the step's own sockets would shadow it. Under version 2 the marker
      // gives that route `top_pick::dynamic`, disjoint from the fixed `top_pick`,
      // so the name is usable — which is the whole point of the marker, and the
      // reason this check is version-conditioned rather than on the field.
      if (socketIdVersion(config) >= CURRENT_SOCKET_ID_VERSION) return;

      config.customerRoutes?.forEach((route, index) => {
        if (
          (IDENTIFY_CUSTOMER_RESERVED_SOCKET_IDS as readonly string[]).includes(
            route.id,
          )
        ) {
          ctx.addIssue({
            code: "custom",
            message: `Route id cannot be one of the reserved socket ids: ${IDENTIFY_CUSTOMER_RESERVED_SOCKET_IDS.join(", ")}`,
            path: ["customerRoutes", index, "id"],
          });
        }
      });
    }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },

  /**
   * Eval config — drives the Test Suite tab on the node and the
   * IdentifyCustomerEvaluator that runs cases through the live handler.
   *
   * The thing we score on is `matched_customer_id` (the AR_CUSTOMER id
   * the pipeline picked, or null when below the confidence threshold).
   * The handler also emits `matched_customer_name`, `confidence_band`,
   * `match_score`, and `matching_reasoning` as flat top-level fields so the
   * test suite UI and workflow run artifacts can show context next to the id.
   */
  evalConfig: {
    evalOutputKeys: ["matched_customer_id"],
    outputFormat: "object",
    contextOutputKeys: [
      "matched_customer_name",
      "confidence_band",
      "match_score",
      "matching_reasoning",
      "resolution",
      "closest_candidate_customer_id",
      "closest_candidate_customer_name",
    ],
  },
});
